import { Controller } from "@hotwired/stimulus"

// Keeps an export link's href in sync with the active filter state.
//
// The export link lives inside its search form. There are two paths that keep
// its href current:
//
//   1. Form submit (the reliable path). When the form is submitted we rebuild
//      the href from the form's own FormData. This reflects exactly what is
//      being submitted (q[name_cont], q[...][] arrays, etc.) and does not
//      depend on event timing. On index pages whose form submits INTO a results
//      turbo-frame, turbo:frame-render and Turbo's address-bar advance race, so
//      reading window.location.search alone is unreliable — deriving from the
//      form removes that race.
//
//   2. URL-settled events (connect, turbo:load, popstate, turbo:frame-render).
//      These cover the INITIAL page state and history navigation, where
//      window.location.search is already settled and safe to read.
//
// In both cases the href becomes:
//   <base>.csv?<query string>   (the "?" is omitted when there are no filters)
//
// Reusable: set data-export-link-base-value to the export path (e.g. "/missions").
export default class extends Controller {
  static values = { base: String }

  connect() {
    this.updateHrefFromLocation = this.updateHrefFromLocation.bind(this)
    this.updateHrefFromForm = this.updateHrefFromForm.bind(this)

    this.updateHrefFromLocation()

    this.form = this.element.closest("form")
    if (this.form) this.form.addEventListener("submit", this.updateHrefFromForm)

    document.addEventListener("turbo:load", this.updateHrefFromLocation)
    document.addEventListener("turbo:frame-render", this.updateHrefFromLocation)
    window.addEventListener("popstate", this.updateHrefFromLocation)
  }

  disconnect() {
    if (this.form) this.form.removeEventListener("submit", this.updateHrefFromForm)

    document.removeEventListener("turbo:load", this.updateHrefFromLocation)
    document.removeEventListener("turbo:frame-render", this.updateHrefFromLocation)
    window.removeEventListener("popstate", this.updateHrefFromLocation)
  }

  // Derives the href from the active query string in the address bar. Safe for
  // the initial state and history navigation, where the URL is already settled.
  updateHrefFromLocation() {
    this.setHref(window.location.search.replace(/^\?/, ""))
  }

  // Derives the href from the form's own values at submit time. FormData
  // reflects exactly what is being submitted, so there is no dependence on when
  // the address bar advances.
  updateHrefFromForm() {
    const form = this.form || this.element.closest("form")
    if (!form) return

    this.setHref(new URLSearchParams(new FormData(form)).toString())
  }

  setHref(queryString) {
    if (!this.hasBaseValue || this.baseValue === "") return

    const suffix = queryString ? `?${queryString}` : ""
    this.element.setAttribute("href", `${this.baseValue}.csv${suffix}`)
  }
}
